import {
  type DocumentData,
  type Firestore,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import {
  InvoiceSchema,
  InvoiceCreateInputSchema,
  InvoiceUpdateInputSchema,
  type Invoice,
  type InvoiceCreateInput,
  type InvoiceUpdateInput,
} from '@taki/data-model';
import { CURRENT_SCHEMA_VERSION, buildUpdateData, createConverter } from './standard-fields.js';
import { type CountOptions, type ListOptions, runCount, runList } from './list-query.js';
import { createArchiveOps } from './archive-ops.js';

/**
 * Repository de la famille `invoice` (document de facturation, org-wide).
 *
 * Calque le patron `asset` : l'entrée est VALIDÉE par Zod
 * (`InvoiceCreateInputSchema`) AVANT toute écriture Firestore. Les champs
 * standard sont remplis par le SDK, les requêtes `list` sont cadrées par orgId.
 */

export type { InvoiceCreateInput };

/** Nom de la collection Firestore de la famille invoice. */
export const INVOICES_COLLECTION = 'invoices';

/**
 * Repository typé pour la famille `invoice`.
 * Toute lecture est validée par `InvoiceSchema`, toute écriture par
 * `InvoiceCreateInputSchema` (cage IA : aucun accès Firestore direct hors SDK).
 */
export interface InvoiceRepository {
  create(input: InvoiceCreateInput): Promise<Invoice>;
  update(id: string, patch: InvoiceUpdateInput): Promise<Invoice>;
  list(options?: ListOptions): Promise<Invoice[]>;
  count(options?: CountOptions): Promise<number>;
  get(id: string): Promise<Invoice | null>;
  /** Archive (`archivedAt`). Semantique commune : cf. archive-ops.ts. */
  archive(id: string): Promise<Invoice>;
  /** Desarchive (`archivedAt` = null, JAMAIS deleteField). Cf. archive-ops.ts. */
  unarchive(id: string): Promise<Invoice>;
  /** Supprime (`deletedAt` + `archivedAt`), invisible cote client. Cf. archive-ops.ts. */
  remove(id: string): Promise<Invoice>;
}

const invoiceConverter = createConverter<Invoice>((raw: DocumentData) => InvoiceSchema.parse(raw));

/**
 * @param getActorId - retourne l'uid de l'utilisateur authentifié courant
 *   (injecté par `createTakiSdk`). Les Security Rules exigent
 *   createdBy/updatedBy == request.auth.uid.
 */
export function createInvoiceRepository(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
): InvoiceRepository {
  const col = collection(db, INVOICES_COLLECTION).withConverter(invoiceConverter);
  // Archiver, desarchiver, supprimer : la semantique des trois etats vit dans
  // `createArchiveOps` (archive-ops.ts), ecrite une seule fois pour les 55
  // familles. Une famille n'a plus rien a decider ici.
  const archiveOps = createArchiveOps<Invoice>(db, col, INVOICES_COLLECTION, getActorId);

  return {
    async create(input: InvoiceCreateInput): Promise<Invoice> {
      // Valider AVANT d'écrire : une entrée invalide échoue ici, rien n'atteint
      // Firestore.
      const fields = InvoiceCreateInputSchema.parse(input);

      const actor = getActorId();
      const ref = doc(col);
      const now = new Date();

      // Champs métier optionnels : on ne les écrit que s'ils sont fournis
      // (évite d'ancrer des `undefined` / `null` parasites dans Firestore).
      const optional: DocumentData = {};
      if (fields.direction !== undefined) optional['direction'] = fields.direction;
      if (fields.number !== undefined) optional['number'] = fields.number;
      if (fields.lines !== undefined) optional['lines'] = fields.lines;
      if (fields.dueAt !== undefined) optional['dueAt'] = fields.dueAt;
      if (fields.status !== undefined) optional['status'] = fields.status;
      if (fields.totalInclTax !== undefined) optional['totalInclTax'] = fields.totalInclTax;
      if (fields.orderId !== undefined) optional['orderId'] = fields.orderId;
      if (fields.paymentId !== undefined) optional['paymentId'] = fields.paymentId;
      if (fields.subscriptionId !== undefined) optional['subscriptionId'] = fields.subscriptionId;
      if (fields.projectId !== undefined) optional['projectId'] = fields.projectId;
      if (fields.customFields !== undefined) optional['customFields'] = fields.customFields;

      const payload: DocumentData = {
        orgId: fields.orgId,
        type: fields.type,
        contactId: fields.contactId,
        ...optional,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: actor,
        updatedBy: actor,
        archivedAt: null,
        deletedAt: null,
        schemaVersion: CURRENT_SCHEMA_VERSION,
      };

      await setDoc(ref, payload as unknown as Invoice);

      // setDoc ne résout pas serverTimestamp() : on valide une projection
      // cohérente avec ce qui vient d'être écrit.
      return InvoiceSchema.parse({
        id: ref.id,
        ...payload,
        createdAt: now,
        updatedAt: now,
      });
    },

    async update(id: string, patch: InvoiceUpdateInput): Promise<Invoice> {
      // Valider AVANT d'écrire : même doctrine que create.
      const fields = InvoiceUpdateInputSchema.parse(patch);
      const actor = getActorId();

      // Seules les clés réellement fournies sont écrites (patch partiel, merge).
      // Patch transforme pour updateDoc : customFields est eclate en chemins
      // pointes ('customFields.<cle>') pour fusionner cle par cle au lieu de
      // remplacer la map entiere (cf. buildUpdateData ; null = deleteField).
      const data = buildUpdateData(fields);
      data['updatedAt'] = serverTimestamp();
      data['updatedBy'] = actor;

      // Ref brute (sans converter) pour l'écriture partielle : une ref convertie
      // exigerait le document complet.
      await updateDoc(doc(db, INVOICES_COLLECTION, id), data);

      // Relire via la ref convertie : le document réel est la source de vérité,
      // et le converter le valide avec InvoiceSchema.
      const snap = await getDoc(doc(col, id));
      if (!snap.exists()) {
        throw new Error(`invoice ${id} introuvable après mise à jour.`);
      }
      return snap.data();
    },

    async list(options?: ListOptions): Promise<Invoice[]> {
      // Cadrage orgId, exclusion des archives, sous-type, tri, taille de page
      // et curseur : toute la semantique vit dans `runList` (list-query.ts),
      // ecrite une seule fois pour les 55 familles.
      return runList(col, orgId, options);
    },

    async count(options?: CountOptions): Promise<number> {
      // Compter sans charger : meme cadrage que `list`, sans page ni tri
      // (cf. runCount, list-query.ts). Un compteur calcule sur la page
      // courante ment des que la liste est paginee.
      return runCount(col, orgId, options);
    },

    async get(id: string): Promise<Invoice | null> {
      const ref = doc(col, id);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      return snap.data();
    },

    // archive / unarchive / remove : cf. `archiveOps` plus haut.
    ...archiveOps,
  };
}
